import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, dynamicLabel } from "../lib/api";
import { FALLBACK_PHOTO } from "../lib/ui";
import { AdmirersSection } from "../components/AdmirersSection";

const MatchCard = ({ match: m, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: index * 0.08 }}
  >
    <Link
      to={`/chat/${m.match_id}`}
      data-testid={`match-card-${m.match_id}`}
      className="block bg-[#F3F0EA] border border-[#E5E0D8] hover:border-[#7A3B3F] transition-colors duration-300"
    >
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={m.user.photo_url || FALLBACK_PHOTO}
          alt={m.user.name}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-6">
        <h3 className="font-serif text-2xl">
          {m.user.name}, {m.user.age}
        </h3>
        <p className="mt-1 text-xs uppercase tracking-[0.15em] text-[#7A3B3F]">
          {dynamicLabel(m.user.relationship_dynamic)}
        </p>
        <p className="mt-3 text-sm text-[#5C5C5C] truncate">
          {m.last_message || "Say hello — you both said yes."}
        </p>
      </div>
    </Link>
  </motion.div>
);

export default function Matches() {
  const [matches, setMatches] = useState(null);
  const [admirers, setAdmirers] = useState(null);
  const navigate = useNavigate();

  const loadMatches = useCallback(
    () => api.get("/matches").then((res) => setMatches(res.data)),
    [setMatches]
  );
  const loadAdmirers = useCallback(
    () => api.get("/likes/received").then((res) => setAdmirers(res.data)),
    [setAdmirers]
  );

  useEffect(() => {
    loadMatches();
    loadAdmirers();
  }, [loadMatches, loadAdmirers]);

  const likeBack = async (p) => {
    const { data } = await api.post("/swipe", { target_id: p.id, action: "like" });
    if (data.matched) {
      toast.success(`It's a match with ${data.match_name}!`, {
        action: { label: "Say hello", onClick: () => navigate(`/chat/${data.match_id}`) },
        duration: 8000,
      });
      await loadMatches();
    }
    loadAdmirers();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16" data-testid="matches-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A3B3F]">Matches</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-4">
        Mutual intention.
      </h1>

      <AdmirersSection admirers={admirers} onLikeBack={likeBack} />

      <div className="mt-16">
        {matches === null && <p className="text-[#8A8A8A]">Loading…</p>}
        {matches?.length === 0 && (
          <div className="py-24 text-center" data-testid="matches-empty">
            <p className="font-serif text-2xl text-[#5C5C5C]">No matches yet.</p>
            <p className="mt-3 text-[#8A8A8A]">
              Keep exploring —{" "}
              <Link to="/discover" className="text-[#7A3B3F] underline underline-offset-4">
                someone is looking for you too
              </Link>
              .
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {matches?.map((m, i) => (
            <MatchCard key={m.match_id} match={m} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
